import React, { useEffect, useState } from 'react';
import { ProjectRow } from '../../lib/types';
import PublicLayout from '../components/layout/public-layout';
import { useAuth } from '../hooks/useAuth';
import styles from './roadmap.module.css';

interface ProjectMetrics {
  count: number;
  recent?: ProjectRow[];
}

export default function RoadmapPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [groupByQuarter, setGroupByQuarter] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch('/projects');
      const data = await response.json();
      if (data.success) {
        setProjects(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuarter = (date: string): string => {
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const quarter = Math.ceil(month / 3);
    return `Q${quarter}`;
  };

  const getUniqueProducts = (): string[] => {
    const products = new Set<string>();
    projects.forEach(p => {
      if (p.ProductServiceId) {
        products.add(p.ProductServiceId);
      }
    });
    return Array.from(products).sort();
  };

  // Filter projects by selected product
  const filteredProjects = selectedProduct === 'all'
    ? projects
    : projects.filter(p => p.ProductServiceId === selectedProduct);

  // Categorize projects by phase
  const governance = filteredProjects.filter(p =>
    ['ReadyForGovernance', 'UnderGovernanceReview', 'BelowTheLine', 'AboveTheLine'].includes(p.Status)
  );

  const portfolio = filteredProjects.filter(p =>
    ['InValidation', 'InPlanning', 'ReadyForDelivery', 'InDelivery', 'Complete'].includes(p.Status)
  );

  // Group by quarter if toggled
  const groupProjectsByQuarter = (items: ProjectRow[]) => {
    if (!groupByQuarter) {
      return { 'All': items };
    }

    const grouped: { [key: string]: ProjectRow[] } = {};
    items.forEach(item => {
      const quarter = getQuarter(item.CreatedDate);
      if (!grouped[quarter]) {
        grouped[quarter] = [];
      }
      grouped[quarter].push(item);
    });
    return grouped;
  };

  const governanceByQuarter = groupProjectsByQuarter(governance);
  const portfolioByQuarter = groupProjectsByQuarter(portfolio);

  const isAuthenticated = !!user;
  const products = getUniqueProducts();

  return (
    <PublicLayout onLoginClick={() => window.location.href = '#/login'}>
      <div className={styles.roadmapContainer}>
        <div className={styles.header}>
          <h1>Product Roadmap</h1>
          <p className={styles.subtitle}>
            See what's planned and being delivered
          </p>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading roadmap...</div>
        ) : (
          <>
            {/* Filters */}
            <div className={styles.filters}>
              {products.length > 0 && (
                <div className={styles.filterGroup}>
                  <label htmlFor="product-filter">Filter by Product/Service:</label>
                  <select
                    id="product-filter"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className={styles.select}
                  >
                    <option value="all">All Products/Services</option>
                    {products.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className={styles.filterGroup}>
                <label>
                  <input
                    type="checkbox"
                    checked={groupByQuarter}
                    onChange={(e) => setGroupByQuarter(e.target.checked)}
                  />
                  {' '}Group by Quarter
                </label>
              </div>
            </div>

            <div className={styles.phasesGrid}>
            {/* Governance Phase */}
            <div className={styles.phase}>
              <div className={styles.phaseHeader}>
                <h2 className={styles.phaseTitle}>
                  <span className={styles.phaseIcon}>🎯</span>
                  Planning
                </h2>
                <span className={styles.count}>{governance.length}</span>
              </div>
              <p className={styles.phaseDescription}>
                Ideas being assessed for feasibility and strategic fit
              </p>

              {governance.length === 0 ? (
                <div className={styles.projectsList}>
                  <p className={styles.empty}>No projects in planning</p>
                </div>
              ) : (
                Object.entries(governanceByQuarter).map(([quarter, items]) => (
                  <div key={quarter} className={styles.quarterGroup}>
                    {groupByQuarter && <h3 className={styles.quarterLabel}>{quarter}</h3>}
                    <div className={styles.projectsList}>
                      {items.slice(0, 5).map(project => (
                        <div key={project.ProjectId} className={styles.projectItem}>
                          <div className={styles.projectTitleRow}>
                            <h3 className={styles.projectTitle}>{project.Title}</h3>
                            <span className={styles.status}>{project.Status}</span>
                          </div>
                          <p className={styles.projectDescription}>
                            {project.Description.substring(0, 100)}...
                          </p>
                          <div className={styles.projectMeta}>
                            <span className={styles.domain}>{project.Domain}</span>
                            {isAuthenticated && project.Priority && (
                              <span className={styles.priority}>{project.Priority}</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {items.length > 5 && (
                        <p className={styles.moreItems}>+{items.length - 5} more projects</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Portfolio Phase */}
            <div className={styles.phase}>
              <div className={styles.phaseHeader}>
                <h2 className={styles.phaseTitle}>
                  <span className={styles.phaseIcon}>📦</span>
                  In Delivery
                </h2>
                <span className={styles.count}>{portfolio.length}</span>
              </div>
              <p className={styles.phaseDescription}>
                Approved projects currently in progress or complete
              </p>

              {portfolio.length === 0 ? (
                <div className={styles.projectsList}>
                  <p className={styles.empty}>No active projects</p>
                </div>
              ) : (
                Object.entries(portfolioByQuarter).map(([quarter, items]) => (
                  <div key={quarter} className={styles.quarterGroup}>
                    {groupByQuarter && <h3 className={styles.quarterLabel}>{quarter}</h3>}
                    <div className={styles.projectsList}>
                      {items.slice(0, 5).map(project => (
                        <div key={project.ProjectId} className={styles.projectItem}>
                          <div className={styles.projectTitleRow}>
                            <h3 className={styles.projectTitle}>{project.Title}</h3>
                            <span className={styles.status}>{project.Status}</span>
                          </div>
                          <p className={styles.projectDescription}>
                            {project.Description.substring(0, 100)}...
                          </p>
                          <div className={styles.projectMeta}>
                            <span className={styles.domain}>{project.Domain}</span>
                            {isAuthenticated && project.ProjectManagerEmail && (
                              <span className={styles.pm}>PM assigned</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {items.length > 5 && (
                        <p className={styles.moreItems}>+{items.length - 5} more projects</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>

            {!isAuthenticated && (
              <div className={styles.authPrompt}>
                <p>Sign in to see strategic priorities and resource allocation details</p>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}
